/**
 * Phase 2 Defect 2.5 — companion modal for `useUnsavedChangesGuard`.
 *
 * When the guard blocks a client-side navigation, this modal asks the user
 * whether to discard the pending changes and leave, or stay and finish.
 *
 * Rendered as a portal on `document.body` so it lives above any card /
 * overlay styling on the underlying form.
 */
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import type { UnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';

export interface UnsavedChangesModalProps {
  guard: UnsavedChangesGuard;
  /** Optional customisation of the modal copy. */
  title?: string;
  body?: string;
  proceedLabel?: string;
  stayLabel?: string;
}

export function UnsavedChangesModal({
  guard,
  title = 'Leave without saving?',
  body = 'You have unsaved changes on this page. If you leave now, your edits will be lost.',
  proceedLabel = 'Discard & leave',
  stayLabel = 'Stay on page',
}: UnsavedChangesModalProps) {
  if (!guard.blocked) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsaved-changes-title"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={guard.reset}
        aria-hidden
      />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="space-y-2">
            <h3 id="unsaved-changes-title" className="text-sm font-semibold text-ink-primary">
              {title}
            </h3>
            <p className="text-xs text-ink-muted">{body}</p>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={guard.reset}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            {stayLabel}
          </button>
          <button
            type="button"
            onClick={guard.proceed}
            className="rounded bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
          >
            {proceedLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
